import axios from "axios";
import { API_BASE_URL, API_PREFIX } from "@/constants";
import type { ApiResponse, AuthTokens } from "@/types";

// Where the user app keeps its session. localStorage is the single source of truth: the axios
// interceptor reads and writes it, and AuthProvider only holds the loaded user (no token state),
// so a silent refresh needs no React state update.
export const ACCESS_TOKEN_KEY = "access_token";
export const REFRESH_TOKEN_KEY = "refresh_token";

// Held by the tab that is exchanging the refresh token, so two tabs never spend the same token
// (the backend rotates it and treats a second use as theft: every session is revoked).
const REFRESH_LOCK = "pwc-token-refresh";

export function storeTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// Session is over: drop the tokens and tell AuthProvider (SPA-friendly, no hard navigation).
// Protected routes then redirect to /login?redirect=<current page>.
export function endSession() {
  clearTokens();
  window.dispatchEvent(new CustomEvent("auth:logout"));
}

// "Bearer xxx" header of a request that was already sent -> "xxx" (null for a guest request)
export function bearerTokenOf(authorization: unknown): string | null {
  return typeof authorization === "string" && authorization.startsWith("Bearer ") ? authorization.slice(7) : null;
}

function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  // Web Locks need a secure context; without it only the per-tab promise below protects rotation
  return typeof navigator !== "undefined" && navigator.locks ? navigator.locks.request(REFRESH_LOCK, task) : task();
}

// Plain axios on purpose: no interceptors, so this call can never trigger another refresh.
async function exchangeRefreshToken(staleToken: string): Promise<string> {
  // Another request or tab may have renewed the token while this one waited for its turn.
  const current = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (current && current !== staleToken) return current;

  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) throw new Error("No refresh token");

  const { data } = await axios.post<ApiResponse<AuthTokens>>(`${API_BASE_URL}${API_PREFIX}/auth/refresh`, {
    refresh_token: refreshToken,
  });
  const tokens = data.data;
  if (!tokens?.access_token || !tokens.refresh_token) throw new Error("Refresh response has no tokens");

  // Logged out (or signed in again) while the request was in flight: do not bring the old session back.
  if (localStorage.getItem(REFRESH_TOKEN_KEY) !== refreshToken) throw new Error("Session changed during refresh");

  storeTokens(tokens.access_token, tokens.refresh_token);
  return tokens.access_token;
}

let inFlight: Promise<string> | null = null;

// Returns a valid access token after the request that carried `staleToken` was refused with 401.
// Concurrent callers share one backend call. Rejects when the session cannot be renewed.
export function renewAccessToken(staleToken: string): Promise<string> {
  const current = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (current && current !== staleToken) return Promise.resolve(current);

  if (!inFlight) {
    inFlight = withRefreshLock(() => exchangeRefreshToken(staleToken)).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
